import type { ShoppingList } from '@domain/list'
import { Botao } from '@ui/glass/Botao'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'

/** Uma lista com a resposta pronta: este item já está lá? */
export type ListaComEstado = {
  lista: ShoppingList
  jaEsta: boolean
}

/**
 * Em qual lista pôr isto.
 *
 * ## Ela mostra TODAS, e não só as que cabem
 *
 * A lista que já tem o item aparece marcada e desabilitada, em vez de sumir —
 * a mesma regra do `LinhaDeSugestao` e do próprio `BotaoLembrar`: *"sumir faria
 * parecer que o botão nunca existiu"*. E aqui ela responde uma pergunta que
 * some junto com o item: **em qual lista isto já está**. Com duas listas, essa
 * é metade da informação que se veio buscar.
 *
 * ## E ela só aparece com DUAS ou mais
 *
 * Com uma lista só, perguntar em qual pôr é pedir para confirmar o óbvio. Quem
 * chama decide isso — ver `BotaoLembrar` —, e por isso este componente não sabe
 * contar listas: ele desenha o que recebe.
 */
export function EscolherLista({
  texto,
  listas,
  gravandoEm,
  onEscolher,
  onFechar,
}: {
  /** O que vai ser adicionado. Vai no título, porque a folha some do contexto. */
  texto: string
  listas: ListaComEstado[]
  /** O id da lista em que a gravação está em curso, se houver. */
  gravandoEm: string | null
  onEscolher: (lista: ShoppingList) => void
  onFechar: () => void
}) {
  return (
    <FolhaDeBaixo titulo={`Em qual lista pôr ${texto}`} onFechar={onFechar}>
      <h2 className="titulo mb-1 text-base">Em qual lista?</h2>
      {/* O nome do item na folha: ela cobre a tela, e sem ele resta uma pergunta
          sem sujeito para quem se distraiu no meio do gesto. */}
      <p className="mb-3 truncate text-sm text-[var(--text-muted)]">{texto}</p>

      <ul>
        {listas.map(({ lista, jaEsta }) => (
          <li key={lista.id}>
            <button
              type="button"
              disabled={jaEsta || gravandoEm !== null}
              aria-label={jaEsta ? `${lista.name} — já está nesta lista` : `Pôr em ${lista.name}`}
              onClick={() => {
                onEscolher(lista)
              }}
              className="alvo flex w-full items-center gap-2 border-b border-[var(--border)] py-2 text-left text-sm disabled:opacity-45"
            >
              <span aria-hidden="true" className="text-lg">
                {lista.icon}
              </span>
              <span className="min-w-0 flex-1 truncate">{lista.name}</span>
              {jaEsta && (
                <span className="text-xs text-[var(--text-muted)]">
                  <span aria-hidden="true">✓ </span>
                  já está
                </span>
              )}
              {gravandoEm === lista.id && (
                <span className="text-xs text-[var(--text-muted)]">pondo…</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-4">
        <Botao type="button" onClick={onFechar}>
          Fechar
        </Botao>
      </div>
    </FolhaDeBaixo>
  )
}
